import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Github, Linkedin, Twitter } from 'lucide-react';
import { profileData, statsData, techStackData } from '../data/mock';

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
    }
  };

  return (
    <section id="home" className="min-h-screen relative pt-20 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 grid-pattern" />
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#00ff88]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00ccff]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[70vh]"
        >
          {/* Left Content */}
          <div className="space-y-8">
            {/* Status Badge */}
            <motion.div variants={itemVariants}>
              <span className="status-badge">
                Available for work
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.div variants={itemVariants}>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1]">
                <span className="text-white">Hello, I'm</span>
                <br />
                <span className="gradient-text">{profileData.firstName}</span>
                <span className="text-white"> {profileData.lastName}</span>
              </h1>
            </motion.div>

            {/* Title */}
            <motion.div variants={itemVariants}>
              <h2 className="text-xl md:text-2xl text-white/60 font-light">
                {profileData.title}
              </h2>
            </motion.div>

            {/* Description */}
            <motion.p 
              variants={itemVariants}
              className="text-white/50 text-lg max-w-xl leading-relaxed"
            >
              {profileData.tagline}
            </motion.p>

            {/* Location */}
            <motion.div 
              variants={itemVariants}
              className="flex items-center gap-2 text-white/40"
            >
              <MapPin size={16} className="text-[#00ff88]" />
              <span className="text-sm">{profileData.location}</span>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-wrap gap-4 pt-4"
            >
              <motion.a
                href="#portfolio"
                className="btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Explore My Work
                <ArrowRight size={18} />
              </motion.a>
              <motion.a
                href="#contact"
                className="btn-secondary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get In Touch
              </motion.a>
            </motion.div>

            {/* Social Links */}
            <motion.div 
              variants={itemVariants}
              className="flex items-center gap-4 pt-4"
            >
              {[
                { icon: Github, href: profileData.social.github },
                { icon: Linkedin, href: profileData.social.linkedin },
                { icon: Twitter, href: profileData.social.twitter }
              ].map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:text-[#00ff88] hover:border-[#00ff88]/30 transition-all"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* Right Content - Profile Card */}
          <motion.div
            variants={itemVariants}
            className="relative"
          >
            <div className="bento-card p-8">
              {/* Profile Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00ff88] to-[#00ccff] flex items-center justify-center text-2xl font-bold text-black">
                  {profileData.initials}
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold text-white">
                    {profileData.name}
                  </h3>
                  <p className="text-white/50 text-sm">
                    {profileData.location}
                  </p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {statsData.map((stat, index) => (
                  <motion.div
                    key={index}
                    className="text-center p-4 rounded-xl bg-white/5"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="font-display text-2xl font-bold text-white mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-white/40">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Tech Tags */}
              <div className="flex flex-wrap gap-2">
                {['Cybersecurity', 'SIEM', 'Penetration Testing', 'Cloud Security', 'Linux'].map((tech, index) => (
                  <motion.span
                    key={tech}
                    className="tech-tag"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.8 + index * 0.05 }}
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#00ff88]/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#00ccff]/10 rounded-full blur-2xl" />
          </motion.div>
        </motion.div>

        {/* Tech Marquee */}
        <motion.div 
          className="mt-20 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <span className="text-xs text-white/30 uppercase tracking-wider">Tech Stack</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
          <div className="relative">
            <div className="flex animate-marquee">
              {[...techStackData, ...techStackData].map((tech, index) => (
                <span
                  key={index}
                  className="mx-8 text-white/20 font-medium whitespace-nowrap text-lg"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
